import axios from "axios";
import { get, post, remove } from "./base";

export const EVENT_STATUS_MAP = [
  { text: "Low", textColor: "#2E9E5B", bg: "rgba(46, 158, 91, 0.1)" },
  { text: "Medium", textColor: "#FFB800", bg: "rgba(255, 184, 0, 0.1)" },
  { text: "High", textColor: "#FF8C00", bg: "rgba(255, 140, 0, 0.12)" },
  { text: "Critical", textColor: "#FF5454", bg: "rgba(255, 84, 84, 0.12)" },
];

// Severity rank for sorting (higher = more severe, sorts to top).
// NOTE: keys are LOWERCASE — the BE model stores severity lowercase
// (critical/high/medium/low). Always normalize before lookup via severityRank().
export const SEVERITY_RANK = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

// Case-insensitive severity rank lookup. Returns 0 for unknown/empty.
export function severityRank(severity) {
  return SEVERITY_RANK[String(severity || "").toLowerCase()] || 0;
}

// Case-insensitive lookup into EVENT_STATUS_MAP (entries keyed by capitalized
// text, BE severity is lowercase). Falls back to first entry (Low) if unknown.
export function getStatusStyle(severity) {
  const key = String(severity || "").toLowerCase();
  return (
    EVENT_STATUS_MAP.find((e) => e.text.toLowerCase() === key) ||
    EVENT_STATUS_MAP[0]
  );
}

// HSE keywords — H2S / combustible (flammable) gas detection.
// AV-D2: these alarms are ALWAYS top priority + visually distinct, regardless
// of their stored severity value.
const HSE_KEYWORDS = [
  "h2s",
  "h₂s",
  "hydrogen sulfide",
  "hydrogen sulphide",
  "sour gas",
  "combustible",
  "flammable",
  "lel",
  "gas detection",
  "gas detector",
  "explosive",
  // Chinese terms
  "硫化氢",
  "可燃气",
  "易燃",
];

// Chinese keywords are case-preserved; ASCII tokens matched case-insensitively.
const HSE_KEYWORDS_LOWER = HSE_KEYWORDS.map((k) => k.toLowerCase());

// AV-D2: classify an alarm as HSE (safety).
// Authoritative signal first: the BE model's `category` field === 'hse'.
// Fallback: keyword scan over tag / event_type / parameter / description text.
export function isHseAlarm(alarm) {
  if (!alarm) return false;
  if (String(alarm.category || "").toLowerCase() === "hse") return true;
  const haystack = [
    alarm.tag,
    alarm.event_type,
    alarm.parameter,
    alarm.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return HSE_KEYWORDS_LOWER.some((kw) => haystack.includes(kw));
}

// AV-D2/AV-D3: comparator — HSE first, then severity, then most-recent date.
export function compareAlarms(a, b) {
  const hseA = isHseAlarm(a) ? 1 : 0;
  const hseB = isHseAlarm(b) ? 1 : 0;
  if (hseA !== hseB) return hseB - hseA; // HSE pinned to top

  const sevA = severityRank(a.severity);
  const sevB = severityRank(b.severity);
  if (sevA !== sevB) return sevB - sevA; // higher severity first

  const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
  const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
  return dateB - dateA; // most recent first
}

export function getAssetAlarms(assetsId, onSuccess, onError) {
  get(
    `/api/assets/${assetsId}/alarms`,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}

export function getAlarms(uwi, onSuccess, onError) {
  get(
    `/api/events/alarms/${uwi}`,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}

// AV-D3: acknowledge an alarm. Persists ack_by / ack_time on the BE
// (POST /api/events/alarms/<id>/ack) and returns the updated alarm row so the
// table can show the real acknowledger + timestamp instead of a local-only
// optimistic flag. hideError=true so the caller can fall back to optimistic
// local state if the endpoint is unavailable, without a blocking error modal.
export function ackAlarm(alarmId, onSuccess, onError) {
  post(
    `/api/events/alarms/${alarmId}/ack`,
    {},
    null,
    {},
    (res) => {
      onSuccess && onSuccess(res.data);
    },
    onError,
    false,
    true
  );
}

export function saveAlarms(alarms, onSuccess, onError) {
  post(
    "/api/events/alarms/",
    alarms,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError,
    true
  );
}

export function removeAlarms(
  eventIds,
  wellUwis,
  eventType,
  onSuccess,
  onError
) {
  let params = {};
  if (eventIds != null && eventIds.length > 0) {
    params.event_ids = eventIds;
  }
  if (wellUwis && wellUwis.length > 0) {
    params.well_uwis = wellUwis;
  }
  if (eventType != null) {
    params.event_type = eventType;
  }

  remove(
    "/api/events/alarms/",
    params,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}

export function getAssetWellEvents(assetsId, onSuccess, onError) {
  get(
    `/api/assets/${assetsId}/events`,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}

export function getWellEvents(uwi, onSuccess, onError) {
  get(
    `/api/events/wells/${uwi}`,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}

export function saveWellEvents(events, onSuccess, onError) {
  post(
    "/api/events/wells/",
    events,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError,
    true
  );
}

export function removeWellEvents(eventIds, wellUwis, onSuccess, onError) {
  let params = {};
  if (eventIds != null && eventIds.length > 0) {
    params.event_ids = eventIds;
  }
  if (wellUwis && wellUwis.length > 0) {
    params.well_uwis = wellUwis;
  }

  remove(
    "/api/events/wells/",
    params,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}

// AV-D6: unified event timeline + Planning Gantt. ONE BE aggregation endpoint
//   GET /api/assets/<asset_id>/events/timeline
// projects the three (un-merged) tables into a single chronological stream and
// a Gantt-ready planning block. Response shape (see common/events.py):
//   {
//     timeline: [ { temporal:'past'|'present'|'future',
//                   type:'event_log'|'alarm'|'planning',
//                   id, event_type, date, well_name, asset_name,
//                   status, severity, detail:{...full source row} }, ... ],
//     gantt:    [ { id, well_name, uwi, event_type, priority,
//                   expected_uplift, cost, currency, resource,
//                   bar:{ start, end, duration, duration_unit } }, ... ]
//   }
// We call axios directly (not the shared get()) because the shared get() ALWAYS
// pops a blocking error Modal on failure — wrong here: if the route isn't
// deployed yet a 404/network error must degrade quietly to an empty-state /
// client-side merge. The global request interceptor in base.js still injects
// the auth token.
export function getAssetEventTimeline(assetId, onSuccess, onError) {
  return axios
    .get(`/api/assets/${assetId}/events/timeline`)
    .then((res) => onSuccess(res.data))
    .catch((error) => onError && onError(error));
}

export function getAssetPlannings(assetsId, onSuccess, onError) {
  get(
    `/api/assets/${assetsId}/plannings`,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}

export function getPlanning(uwi, onSuccess, onError) {
  get(
    `/api/events/plannings/${uwi}`,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}

export function savePlanning(events, onSuccess, onError) {
  post(
    "/api/events/plannings/",
    events,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError,
    true
  );
}

export function removePlanning(
  eventIds,
  wellUwis,
  eventType,
  onSuccess,
  onError
) {
  let params = {};
  if (eventIds != null && eventIds.length > 0) {
    params.event_ids = eventIds;
  }
  if (wellUwis && wellUwis.length > 0) {
    params.well_uwis = wellUwis;
  }
  if (eventType != null) {
    params.event_type = eventType;
  }

  remove(
    "/api/events/plannings/",
    params,
    null,
    {},
    (res) => {
      onSuccess(res.data);
    },
    onError
  );
}
